import FrontlinePage from "./pages/Frontline/FrontlinePage";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import LoginPage from "./pages/LoginPage";
import SocialLoginPage from "./pages/SocialLoginPage";
import { lazy, Suspense } from "react";
const Dashboard = lazy(() => import("./pages/Dashboard"));
import PlaceholderPage from "./pages/PlaceholderPage";
import ConditionGroupsLookupsPage from "./pages/ConditionGroupsLookups/ConditionGroupsLookupsPage";
import SpecialitiesPage from "./pages/Specialities/SpecialitiesPage";
import OrganizationsPage from "./pages/Organizations/OrganizationsPage";
import SeedVideosPage from "./pages/SeedVideos/SeedVideosPage";
import SeedResourcesPage from "./pages/SeedResources/SeedResourcesPage";
import SeedPrescriptionsPage from "./pages/SeedPrescriptions/SeedPrescriptionsPage";
import ConditionsPage from "./pages/Conditions/ConditionsPage";
import QrCodesPage from "./pages/QrCodes/QrCodesPage";
import UsersPage from "./pages/Users/UsersPage";
import Layout from "./components/layout/Layout";
import { SidebarLayoutProvider, useSidebarLayout } from "./components/Sidebar/SidebarLayoutContext";
import AgentChatWidget from "./modules/agent-chat/AgentChatWidget";
import { getPostAuthRedirectPath } from "./utils/socialAuth";

// Loading component
const Loading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh' 
  }}>
    Loading...
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated === null) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return children;
  }

  const nextPath = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
};

const PublicAuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated === null) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return children;
  }

  const params = new URLSearchParams(location.search);
  const redirectPath = getPostAuthRedirectPath(params.get("next"));
  return <Navigate to={redirectPath} replace />;
};

const AuthenticatedAgentChat = () => {
  const { width } = useSidebarLayout();
  const { isAuthenticated } = useAuth();

  return isAuthenticated ? <AgentChatWidget maximizeX={width} /> : null;
};

export default function App() {
  return (
    <BrowserRouter>
      <SidebarLayoutProvider>
      <AuthenticatedAgentChat />
      <Routes>
        <Route path="/frontline/:organizationId?" element={<ProtectedRoute><Layout><FrontlinePage /></Layout></ProtectedRoute>} />
        <Route 
          path="/login" 
          element={
            <PublicAuthRoute>
              <LoginPage />
            </PublicAuthRoute>
          } 
        />
        <Route path="/social-login" element={<SocialLoginPage />} />
        <Route 
          path="/dashboard" 
          element={<ProtectedRoute><Layout><Suspense fallback={<Loading />}><Dashboard /></Suspense></Layout></ProtectedRoute>}
        />
        <Route 
          path="/condition-lookup-groups" 
          element={
            <ProtectedRoute>
              <Layout>
                <ConditionGroupsLookupsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/specialities" 
          element={
            <ProtectedRoute>
              <Layout>
                <SpecialitiesPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/organizations" 
          element={
            <ProtectedRoute>
              <Layout>
                <OrganizationsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/seed-videos" 
          element={
            <ProtectedRoute>
              <Layout>
                <SeedVideosPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/seed-resources" 
          element={
            <ProtectedRoute>
              <Layout>
                <SeedResourcesPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/seed-prescriptions" 
          element={
            <ProtectedRoute>
              <Layout>
                <SeedPrescriptionsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/conditions" 
          element={
            <ProtectedRoute>
              <Layout>
                <ConditionsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/qr-codes" 
          element={
            <ProtectedRoute>
              <Layout>
                <QrCodesPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <Layout>
                <Navigate to="/dashboard" replace />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/content" 
          element={
            <ProtectedRoute>
              <Layout>
                <PlaceholderPage title="Content" description="Manage your content and media" />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <PlaceholderPage title="Settings" description="Configure your application settings" />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/help" 
          element={
            <ProtectedRoute>
              <Layout>
                <PlaceholderPage title="Help" description="Get help and support" />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
      </SidebarLayoutProvider>
    </BrowserRouter>
  );
}
